import { useEffect, useRef, useState } from "react"

const TICK_MS = 250
/** Trickle ceiling — real completion (100) only ever comes from the server. */
const CAP_PCT = 95
/** Time constant of the asymptotic curve: ~60% after 30 s, ~83% after 60 s. */
const TAU_MS = 30_000

/**
 * Smooths a coarse server-reported progress percentage into a continuously
 * advancing display value. The analyze-status endpoint only counts finished
 * files, so a single-file import reports 0 until it jumps to 100; this hook
 * trickles along an asymptotic curve toward CAP_PCT in the meantime. The
 * server value acts as a hard floor, the result is monotonically increasing,
 * and 100 is shown only once the server reports it.
 */
export function useSmoothedProgress(serverPct: number): number {
  const [displayPct, setDisplayPct] = useState(() =>
    Math.min(Math.max(serverPct, 0), 100),
  )
  const elapsedRef = useRef(0)

  useEffect(() => {
    if (serverPct >= 100) return undefined
    const id = setInterval(() => {
      elapsedRef.current += TICK_MS
      const trickle = Math.min(
        Math.ceil(CAP_PCT * (1 - Math.exp(-elapsedRef.current / TAU_MS))),
        CAP_PCT,
      )
      setDisplayPct((current) => Math.max(current, serverPct, trickle))
    }, TICK_MS)
    return () => clearInterval(id)
  }, [serverPct])

  if (serverPct >= 100) return 100
  return Math.max(displayPct, serverPct)
}
